'use client';

import React, { useState } from 'react';
import { useHits, useInstantSearch, usePagination, Configure, useSearchBox } from 'react-instantsearch';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import MuiPagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Typography, { TypographyProps } from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ErrorBoundary from '../ErrorBoundary';
import ContentModule from '../ContentModule';
import { CollectionProps } from '../Collection';
import Link from '../Link';
import sidekick from '@last-rev/cms-sidekick-util';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAuthContext } from '../AuthProvider';
import dayjs from 'dayjs';

import CategoryLinks from '../CategoryLinks';
import { useLocalizationContext } from '../LocalizationContext';
import SearchResultsSorting from '../SearchResultsSorting';
import AISearchResult from '../AISearchResult';
// PROJECT-1700: AI search terms gate disabled — uncomment to wrap AISearchSection again
// import AITermsGate from '../AITermsGate';
import { useChatbotSettings, ChatbotSettings } from '../ChatbotSettingsContext';
import { handleTrackingEventName } from '../../utils/handleTrackingEventName';
import { getAlgoliaQueryDefaults } from '../../utils/algoliaQueryDefaults';

const isPRC = process.env.SITE === 'RESOURCE';

// Context to share AI search shown state with Hit components
const AISearchShownContext = React.createContext<boolean>(false);

interface CategoryProps {
  name: string;
  href: string;
}

interface HitProps {
  categories: Array<string>;
  categoryLinks: Array<CategoryProps>;
  path: string;
  summary?: string;
  objectID?: string;
  __position?: string;
  __queryID?: string;
  createdAt?: string;
  updatedAt?: string;
  contentOwner?: string;
  _highlightResult?: any;
}

interface CustomHighlightProps {
  attribute: string;
  hit: Record<string, any> & {
    _highlightResult?: Record<string, { value?: string }>;
  };
  component?: React.ElementType;
  [key: string]: any;
}

// Custom Highlight component to replace react-instantsearch-dom's Highlight
const CustomHighlight = ({ attribute, hit, component = 'span', ...props }: CustomHighlightProps) => {
  const Component = component;
  const highlightResult = hit._highlightResult?.[attribute];

  if (!highlightResult) {
    return <Component {...props}>{hit[attribute]}</Component>;
  }

  const highlightedValue = highlightResult.value || hit[attribute];

  return <Component {...props} dangerouslySetInnerHTML={{ __html: highlightedValue }} />;
};

interface LoadingItemsProps {
  hitsPerPage: number;
}

// TODO: Find proper TS type in Algolia API
const Hit = (props: any) => {
  const hit = props.hit as HitProps;
  const sendEvent = props.sendEvent as ((type: string, hit: any, name: string) => void) | undefined;
  const aiSearchShown = React.useContext(AISearchShownContext);
  const categories = hit.categoryLinks.map((category: CategoryProps) => ({
    text: category.name,
    href: category.href
  }));
  const metadata: string[] = [];

  if (hit.updatedAt) {
    metadata.push(`Updated on ${dayjs(hit.updatedAt).format('MMM D, YYYY')}`);
  }
  // if (hit.createdAt) {
  //   metadata.push(`Created on ${dayjs(hit.createdAt).format('MMM D, YYYY')}`);
  // }
  if (hit.contentOwner && isPRC) {
    metadata.push(`Content owner: ${hit.contentOwner}`);
  }

  const handleHitClick = () => {
    // Algolia first: this is the event that lets ranking improve from what
    // people open. The analytics call below is our own tracking and is separate.
    sendEvent?.('click', hit, 'Hit Clicked');
    handleTrackingEventName({
      trackingEventName: 'Algolia Search Result Click',
      href: hit.path,
      additionalData: { ai_search_shown: aiSearchShown ? 1 : 0 }
    });
  };

  return (
    <div
      data-insights-object-id={hit.objectID}
      data-insights-position={hit.__position}
      data-insights-query-id={hit.__queryID}>
      <Link href={hit.path} onClick={handleHitClick}>
        <HitTitle component={CustomHighlight} variant="body2" attribute="title" hit={hit} />
      </Link>
      {!!metadata.length && (
        <Link
          href={hit.path}
          onClick={handleHitClick}
          sx={{ textDecoration: 'none', alignItems: 'center', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {metadata.map((meta, index) => (
            <React.Fragment key={meta}>
              <Typography variant="caption">{meta}</Typography>
              {index < metadata.length - 1 && <Typography variant="caption">|</Typography>}
            </React.Fragment>
          ))}
        </Link>
      )}
      <Link href={hit.path} onClick={handleHitClick}>
        <HitDescription component={CustomHighlight} variant="body2" attribute="summary" hit={hit} />
      </Link>
      {categories.length > 0 && (
        <Box mt={2} data-testid="CollectionSearchResults-categories">
          <CategoryLinks links={categories} />
        </Box>
      )}
    </div>
  );
};

const LoadingItems = (props: LoadingItemsProps) => {
  return (
    <React.Fragment>
      {[...Array(props.hitsPerPage)].map((_, idx) => (
        <React.Fragment key={`loading-${idx}`}>
          <Typography variant="h5" mb={1}>
            <Skeleton animation="wave" width="80%" />
          </Typography>
          <Skeleton animation="wave" height={12} width="92%" />
          <Skeleton animation="wave" height={12} width="90%" />
          <Box display="flex" mt={1} mb={5}>
            <Skeleton animation="wave" height={28} width={80} sx={{ marginRight: 2 }} />
            <Skeleton animation="wave" height={28} width={60} />
          </Box>
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

const QueryTitle = ({ label }: { label: string }) => {
  const { indexUiState } = useInstantSearch();
  const query = indexUiState.query;

  return query ? (
    <Typography variant="h3" component="h3" mb={2}>
      {`${label} ${query}`}
    </Typography>
  ) : null;
};

interface SearchBoxProps {
  placeholder?: string;
}

const SearchBoxInput = ({ placeholder }: SearchBoxProps) => {
  const { query, refine } = useSearchBox();
  const [inputValue, setInputValue] = useState(query);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Update URL on submit to trigger AI search refresh while keeping filters
    if (inputValue.trim()) {
      const params = new URLSearchParams(searchParams?.toString());
      params.set('query', inputValue.trim());
      params.delete('page');
      router.push(`${pathname}?${params.toString()}`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Live update Algolia results as user types
    refine(newValue);
  };

  // Sync input value with query from URL on mount
  React.useEffect(() => {
    setInputValue(query);
  }, [query]);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
      <TextField
        fullWidth
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder || 'Search...'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            borderRadius: (theme) => theme.spacing(3.75),
            minHeight: (theme) => theme.spacing(7)
          }
        }}
      />
    </Box>
  );
};

const Pagination = () => {
  const { nbPages, currentRefinement, refine } = usePagination();

  return nbPages > 0 ? (
    <MuiPagination
      variant="outlined"
      shape="rounded"
      count={nbPages}
      page={currentRefinement + 1}
      onChange={(event: React.ChangeEvent<unknown>, value: number) => {
        event.preventDefault();
        refine(value - 1);
        global.window.scrollTo(0, 0);
      }}
      renderItem={(item) => <PaginationItem {...item} />}
    />
  ) : null;
};

interface StateResultsProps {
  resultsForLabel: string;
  noResultsLabel: string;
  sorting?: any;
}

const StateResults = ({ resultsForLabel, noResultsLabel, sorting }: StateResultsProps) => {
  const { results, status, indexUiState } = useInstantSearch();
  const query = indexUiState.query;
  const isSearching = status === 'loading' || status === 'stalled';
  const nbHits = results?.nbHits ?? 0;
  const hitsPerPage = results?.hitsPerPage ?? 8;

  return (
    <>
      {query && nbHits > 0 && !isSearching && (
        <Box display="flex" gap={4} alignItems="center" mb={5}>
          <Typography variant="body2" component="p">
            {`${nbHits} ${resultsForLabel} ${query}`}
          </Typography>
          {sorting && <SearchResultsSorting sorting={sorting} />}
        </Box>
      )}
      {isSearching && <LoadingItems hitsPerPage={hitsPerPage} />}
      {!isSearching && nbHits === 0 && (
        <Typography variant="body2" component="p" mb={5}>
          {noResultsLabel}
        </Typography>
      )}
      <Box display={!isSearching && nbHits !== 0 ? 'block' : 'none'}>
        <CustomHits />
        <Pagination />
      </Box>
    </>
  );
};

const CustomHits = () => {
  /**
   * sendEvent is what actually reports a click to Algolia.
   *
   * The data-insights-object-id, -position and -query-id attributes on each hit
   * are the InstantSearch.js mechanism, and they are inert in React: nothing
   * reads them here. So click-through stayed at zero even with insights enabled
   * and clickAnalytics on the request, because the only event ever sent was the
   * automatic Hits Viewed. In react-instantsearch a click has to be sent by
   * hand, which is this.
   */
  const { items, sendEvent } = useHits();

  return (
    <HitList>
      <ul>
        {items.map((hit) => (
          <li key={hit.objectID}>
            <Hit hit={hit} sendEvent={sendEvent} />
          </li>
        ))}
      </ul>
    </HitList>
  );
};

interface AISearchSectionProps {
  chatbotSettings?: ChatbotSettings;
  aiSearchEnabled: boolean;
  query: string;
}

const AISearchSection = React.memo(function AISearchSection({
  chatbotSettings,
  aiSearchEnabled,
  query
}: AISearchSectionProps) {
  // Don't render if no chatbot settings
  if (!chatbotSettings?.chatflowid || !chatbotSettings?.apiHost) {
    return null;
  }

  return (
    <AISearchResult
      query={query}
      chatflowid={chatbotSettings.chatflowid}
      apiHost={chatbotSettings.apiHost}
      enabled={aiSearchEnabled && !!query.trim()}
    />
  );
});

const IS_SIGNAL_3 = process.env.SITE === 'IAS-KB-SIGNAL3-SITE';

export interface CollectionSearchResultsProps extends CollectionProps {
  settings: any;
}

export const CollectionSearchResults = ({ introText, settings, sidekickLookup }: CollectionSearchResultsProps) => {
  const chatbotSettings = useChatbotSettings();
  const localization = useLocalizationContext();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // PROJECT-1700: was termsAccepted from AITermsGate — restore with AITermsGate import
  // const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
  // Get query from URL search params for stable reference (not affected by pagination)
  const searchQuery = searchParams?.get('query') || '';

  // Extract locale from pathname
  // For default locale: /search/... (no locale in path)
  // For other locales: /de-DE/search/... (locale is first segment)
  const pathSegments = pathname?.split('/').filter(Boolean) || [];
  const potentialLocale = pathSegments[0];
  const defaultLocale = 'en-US';
  // Check if first segment matches locale pattern (xx-XX)
  const isLocaleSegment = /^[a-z]{2}-[A-Z]{2}$/.test(potentialLocale);
  const locale = isLocaleSegment ? potentialLocale : defaultLocale;

  const { apps, botPermission } = useAuthContext();
  const sorting = settings?.sorting;

  const filters: string[] = [`locale:${locale}`];

  if (locale !== defaultLocale) {
    filters.push(`translatedInLocale:true`);
  }
  let facetFilters: string[] = [];

  if (IS_SIGNAL_3) {
    filters.push(`hasSsoGroups:true`);

    if (!!apps?.length) {
      const ssoGroupsFilter = apps.map((app) => `ssoGroups:"${app}"`).join(' OR ');
      filters.push(`(${ssoGroupsFilter})`);
    }
  }

  /* PROJECT-1700: restore with AITermsGate
  const handleTermsStatusChange = (status: 'accepted' | 'declined' | null) => {
    setTermsAccepted(status === 'accepted');
  };
  */

  const aiSearchEnabled = botPermission === true;

  // Determine if AI search is shown for tracking purposes
  const aiSearchShown = Boolean(
    chatbotSettings?.chatflowid && chatbotSettings?.apiHost && aiSearchEnabled && searchQuery.trim()
  );

  return (
    <ErrorBoundary>
      <AISearchShownContext.Provider value={aiSearchShown}>
        <Box mb={6} data-testid="CollectionSearchResults" {...sidekick(sidekickLookup)}>
          {introText ? (
            <ContentModule
              {...introText}
              {...sidekick(sidekickLookup?.introText)}
              data-testid="CollectionSearchResults-introText"
            />
          ) : null}
          <SearchBoxInput placeholder={localization['search.placeholder.label']?.shortTextValue || 'Search...'} />
          <QueryTitle
            label={localization['collections.showingResultsFor.label']?.shortTextValue ?? 'Showing results for'}
          />
          <Configure
            filters={filters.join(' AND ')}
            facetFilters={facetFilters}
            hitsPerPage={8}
            facetingAfterDistinct={true}
            clickAnalytics={true}
            // Empty in production. In a deploy preview this carries the query
            // settings from the pending change, so the fix can be seen against
            // the real index without copying it. Spread last so a preview can
            // override a default above deliberately.
            {...getAlgoliaQueryDefaults()}
          />
          {/* AI Search Results - stays visible across pagination */}
          {chatbotSettings?.chatflowid && chatbotSettings?.apiHost && (
            <>
              {/* PROJECT-1700: <AITermsGate onTermsStatusChange={handleTermsStatusChange}> */}
              <AISearchSection
                chatbotSettings={chatbotSettings}
                aiSearchEnabled={aiSearchEnabled}
                query={searchQuery}
              />
              {/* </AITermsGate> */}
            </>
          )}
          <StateResults
            resultsForLabel={localization['collections.resultsFor.label']?.shortTextValue ?? 'results for'}
            noResultsLabel={localization['collections.noResultsFound.label']?.shortTextValue ?? 'No results found'}
            sorting={sorting}
          />
        </Box>
      </AISearchShownContext.Provider>
    </ErrorBoundary>
  );
};

const HitList = styled('div', {
  name: 'CollectionSearchResults',
  slot: 'Hits'
})(({ theme }) => ({
  '& ul': {
    listStyle: 'none',
    margin: 0,
    padding: 0
  },
  '& li:not(:first-child)': {
    paddingTop: theme.spacing(4.5)
  },
  '& a:hover': {
    textDecoration: 'none'
  }
}));

const HitTitle = styled(Typography, {
  name: 'CollectionSearchResult',
  slot: 'HitTitle'
})<TypographyProps<React.ElementType>>(({ theme }) => ({
  display: 'block',
  paddingBottom: theme.spacing(1),
  fontWeight: 600,
  em: {
    backgroundColor: theme?.palette?.yellow?.main
  }
}));

const HitDescription = styled(Typography, {
  name: 'CollectionSearchResult',
  slot: 'HitDescription'
})<TypographyProps<React.ElementType>>(({ theme }) => ({
  display: 'block',
  color: theme.palette.midnight.A80,
  wordBreak: 'break-word',
  em: {
    fontWeight: 700,
    color: theme.palette.midnight.A100
  }
}));

export default CollectionSearchResults;
